/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, ReactElement } from "react";

import { useDispatch, useSelector } from "react-redux";

import { delay } from "lodash";

import { Spinner, SpinnerSize } from "@fluentui/react";

import { useAuth } from "Authentication/AuthProvider";
import UserProfileDistributionList from "Components/DistributionLists/UserProfileDistributionList";
import { OperationStatus } from "Constants/OperationStatus";
import { defaultDelay } from "Constants/ProfileConstants";
import { PermissionsEnum } from "Constants/RolesPermissionsConstants";
import { TenantTypesEnum } from "Constants/TenantTypes";
import { getSelectedUserProfile } from "Dataflows/MVP/UserProfile/MVPUserProfileSelector";
import {
  userProfileDlLoad,
  userProfileDlRemove,
  userProfileDlRemoveAll,
} from "Dataflows/UserProfile/UserProfileAction";
import {
  getUserProfileDl,
  getUserProfileDlLoadStatus,
  getUserProfileDlRemoveAllError,
  getUserProfileDlRemoveAllStatus,
  getUserProfileDlRemoveError,
  getUserProfileDlRemoveStatus,
} from "Dataflows/UserProfile/UserProfileSelector";
import { IDistributionList } from "Interfaces/UserProfile/DistributionLists/IDistributionLists";

/**
 * The Most Valuable Professional Distribution Lists component
 * @method
 * @returns {ReactElement} The Most Valuable Professional Distribution Lists component
 */
const MostValuableProfessionalDistributionLists = (): ReactElement => {
  const { hasPermission } = useAuth();
  const dispatch = useDispatch();
  const distributionList = useSelector(getUserProfileDl);
  const distributionListLoadStatus = useSelector(getUserProfileDlLoadStatus);
  const distributionListRemoveStatus = useSelector(getUserProfileDlRemoveStatus);
  const distributionListRemoveError = useSelector(getUserProfileDlRemoveError);
  const distributionListRemoveAllStatus = useSelector(getUserProfileDlRemoveAllStatus);
  const distributionListRemoveAllError = useSelector(getUserProfileDlRemoveAllError);
  const userProfileId = useSelector(getSelectedUserProfile)?.id as number;

  const canEditDistributionListMembership = hasPermission(
    TenantTypesEnum.MVP,
    PermissionsEnum.MVP_UPDATE_DISTRIBUTIONS_LIST
  );

  const handleLoadDl = (): void => {
    dispatch(userProfileDlLoad(userProfileId, TenantTypesEnum.MVP));
  };

  const handleRemove = (distributionList: IDistributionList): void => {
    dispatch(
      userProfileDlRemove(
        distributionList.dlDisplayName,
        distributionList.joinedEmail,
        TenantTypesEnum.MVP
      )
    );
  };

  const handleRemoveAll = (): void => {
    dispatch(userProfileDlRemoveAll(userProfileId, TenantTypesEnum.MVP));
  };

  useEffect(() => {
    if (distributionListLoadStatus !== OperationStatus.Pending) {
      handleLoadDl();
    }
  }, []);

  useEffect(() => {
    if (
      distributionListRemoveStatus === OperationStatus.Succeeded ||
      distributionListRemoveAllStatus === OperationStatus.Succeeded
    ) {
      delay(handleLoadDl, defaultDelay);
    }
  }, [distributionListRemoveStatus, distributionListRemoveAllStatus]);

  if (distributionListLoadStatus === OperationStatus.Pending)
    return <Spinner size={SpinnerSize.large} />;

  return (
    <UserProfileDistributionList
      distributionLists={distributionList}
      allowEditing={canEditDistributionListMembership}
      onRemoveDl={handleRemove}
      onRemoveAllDl={handleRemoveAll}
      dlRemoveStatus={distributionListRemoveStatus}
      dlRemoveError={distributionListRemoveError}
      dlRemoveAllStatus={distributionListRemoveAllStatus}
      dlRemoveAllError={distributionListRemoveAllError}
    />
  );
};

export default MostValuableProfessionalDistributionLists;
